"use client";
import React from "react";
import { PageManifestoSchema } from "@/sanity/pages/PageManifestoTemplate";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import Footer from "@/components/global/partials/Footer";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableText } from "next-sanity";
import Image from "@/helpers/Image";
import chart from "@/public/manifesto/chart.svg";
import statue from "@/public/manifesto/lostImage.png";
import lostBg from "@/public/manifesto/lostBg.png";
import quotes from "@/public/manifesto/quotes.svg";
import { motion } from "framer-motion";

export default function LostOportunitySection({
  data,
}: {
  data: PageManifestoSchema;
}) {
  return (
    <>
      <section id="lost">
        <div className="w-[min(94.25rem,100%)] mx-auto flex flex-col gap-[4rem]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0,
              ease: [0.25, 0.4, 0.25, 1],
            }}
            viewport={{ once: true }}
            className="relative overflow-clip bg-[#FFF7E2] rounded-[3rem] px-[2.75rem] lg:px-[4rem] py-[4.75rem] lg:min-h-[39rem]"
          >
            <Image
              src={lostBg}
              alt=""
              className="absolute top-0 right-0 z-0 !h-full !w-[60rem] lg:!w-[58.125rem]"
            />
            <Image
              src={statue}
              alt=""
              className="absolute bottom-0 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-0 z-0 !w-[38rem] lg:!w-[39.625rem]"
            />

            <div className="lg:w-[44.125rem] min-h-[60rem] lg:min-h-[29.5rem] relative z-[1] flex flex-col gap-[1.5rem]">
              {data?.lostTitle && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: 0,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  viewport={{ once: true }}
                  className="richtext richtext-start w-full
                  prose-h2:text-[4rem] lg:prose-h2:text-[4.5rem] prose-h2:mb-[3rem]"
                >
                  <PortableText
                    value={data.lostTitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
              {data?.lostSubtitle && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.3,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  viewport={{ once: true }}
                  className="richtext richtext-start
                  prose-p:text-[2rem] prose-p:text-[#103037]/80 prose-strong:font-normal prose-strong:text-[#103037]"
                >
                  <PortableText
                    value={data.lostSubtitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
              {data?.lostQuote && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.6,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  viewport={{ once: true }}
                  className="flex gap-[2rem] items-start bg-[#50452A0D] border-l border-[#D8CDB0] pl-[2rem] py-[1rem]"
                >
                  <Image
                    src={quotes}
                    alt="quotes"
                    className="!w-[1.946rem] shrink-0 pt-[0.75rem]"
                  />
                  <div
                    className="richtext richtext-start
                    prose-p:text-[2rem] prose-p:text-[#50452A]/90 prose-p:leading-[150%]"
                  >
                    <PortableText
                      value={data.lostQuote}
                      components={richTextComponent}
                    />
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0,
              ease: [0.25, 0.4, 0.25, 1],
            }}
            viewport={{ once: true }}
            className="px-[2rem] lg:px-[7rem]"
          >
            {data?.lostContent && (
              <div
                className="richtext richtext-start gap-[2rem]
                prose-p:my-0 prose-h4:my-0
                prose-strong:font-normal prose-strong:text-black
                [&_.font-gestura]:text-[3rem] [&_.font-gestura]:text-[#07171B] [&_.font-gestura]:font-bold [&_.font-gestura]:tracking-[-0.06rem] [&_.font-gestura]:leading-[112%]
                [&_.faded]:!text-[#07171B]/50"
              >
                <PortableText
                  value={data.lostContent}
                  components={richTextComponent}
                />
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
}
